import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  EmptyContent,
  Flex,
  Heading,
  ScrollArea,
  Separator,
  Text,
} from '@/components/ui';
import {
  generateFormDemoCsv,
  generateFormTemplateCsv,
} from '../utils/csvTemplateGenerator';
import { parseCSVContent } from '@/components/file-preview/utils/csvParser';
import { DataTable, DataTableColumnHeader } from '@/components/data-table';
import { StudyFormHoverCard } from '@/components/data-import/StudyFormHoverCard';
import { DemoPreviewToggle } from './DemoPreviewToggle';
import { DemoDataSettingsPopover } from './DemoDataSettingsPopover';
import type { FormTreeNode } from './utils/formTreeUtils';
import type { StudyForm } from '@/types/studyForms';
import type { DemoDataSettings } from '../types/demoDataSettings';
import { DEFAULT_DEMO_DATA_SETTINGS } from '../types/demoDataSettings';
import { saveDemoDataSettings } from '../utils/demoDataSettingsStorage';
import { IconDownload, IconFileText, IconX } from '@tabler/icons-react';
import { ColumnDef } from '@tanstack/react-table';
import { FORM_TYPE_SUBFORM } from '@/utils/formUtils';

interface CsvPreviewModalProps {
  form: FormTreeNode;
  type: 'template' | 'demo';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDownload: (type: 'template' | 'demo') => void;
  previewWithDemo?: boolean; // Allow dynamic switching
  onPreviewWithDemoChange?: (enabled: boolean) => void; // Callback for toggle change
  allForms?: StudyForm[]; // For hover card
  demoDataSettings?: DemoDataSettings; // Demo data generation settings
  onDemoDataSettingsChange?: (settings: DemoDataSettings) => void; // Callback for settings change
}

export const CsvPreviewModal: React.FC<CsvPreviewModalProps> = ({
  form,
  type: initialType,
  open,
  onOpenChange,
  onDownload,
  previewWithDemo = false,
  onPreviewWithDemoChange,
  allForms = [],
  demoDataSettings: externalDemoDataSettings,
  onDemoDataSettingsChange,
}) => {
  // Dynamic type based on previewWithDemo prop
  const [currentType, setCurrentType] = useState<'template' | 'demo'>(
    initialType
  );

  // Local state for demo data settings (for real-time editing in modal)
  const [localDemoDataSettings, setLocalDemoDataSettings] =
    useState<DemoDataSettings>(
      externalDemoDataSettings || DEFAULT_DEMO_DATA_SETTINGS
    );

  // Update local settings when external settings change
  useEffect(() => {
    if (externalDemoDataSettings) {
      setLocalDemoDataSettings(externalDemoDataSettings);
    }
  }, [externalDemoDataSettings]);

  // Use local settings for demo mode, external for template mode
  const activeDemoDataSettings = previewWithDemo
    ? localDemoDataSettings
    : externalDemoDataSettings;

  // Update type when previewWithDemo changes
  useEffect(() => {
    setCurrentType(previewWithDemo ? 'demo' : 'template');
  }, [previewWithDemo]);

  // Handle settings change in modal
  const handleSettingsChange = (settings: DemoDataSettings) => {
    setLocalDemoDataSettings(settings);
    saveDemoDataSettings(settings);
    // Notify parent component if callback is provided
    if (onDemoDataSettingsChange) {
      onDemoDataSettingsChange(settings);
    }
  };

  // Handle toggle change from modal
  const handleToggleChange = (enabled: boolean) => {
    if (onPreviewWithDemoChange) {
      onPreviewWithDemoChange(enabled);
    }
  };

  // Convert allForms array to Map for StudyFormHoverCard
  const formsMap = useMemo(() => {
    const map = new Map<number, StudyForm>();
    allForms.forEach(f => {
      map.set(f.id, f);
    });
    return map;
  }, [allForms]);

  // Use parent form ID directly from tree
  // The tree structure already shows which parent this form belongs to in the current context
  // Even if form has multiple parents, in the tree it's under a specific parent
  const parentFormId = useMemo(() => {
    if (!form.parent) {
      return undefined;
    }

    // Use the parent ID directly from the tree
    // This ensures we use the correct parent for the current tree context
    return form.parent.id;
  }, [form]);

  // Generate CSV content based on current type
  const csvContent = useMemo(() => {
    return currentType === 'template'
      ? generateFormTemplateCsv(form, parentFormId)
      : generateFormDemoCsv(form, parentFormId, activeDemoDataSettings);
  }, [form, currentType, parentFormId, activeDemoDataSettings]);

  // Parse CSV for preview
  const csvData = useMemo(() => {
    try {
      return parseCSVContent(csvContent, 100);
    } catch (error) {
      console.error('Error parsing CSV:', error);
      return null;
    }
  }, [csvContent]);

  const formDisplayName = form.json_schema?.displayName || form.name;

  // Generate filename with parent suffix if form has parent
  const filename = useMemo(() => {
    const parentName = form.parent?.name;
    const baseName = parentName ? `${form.name} (${parentName})` : form.name;
    const suffix = currentType === 'template' ? '_template.csv' : '_demo.csv';
    return `${baseName}${suffix}`;
  }, [form, currentType]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        fullscreen
        disableScrollArea
        className="csv-preview-modal"
      >
        {/* Header */}
        <Dialog.Header
          title={`CSV Preview: ${formDisplayName}`}
          subtitle={
            currentType === 'template'
              ? 'Empty template CSV file for this form'
              : `Demo CSV file with ${localDemoDataSettings.numberOfRows} sample rows`
          }
          endSlot={
            <Flex align="center" gap="3">
              {onPreviewWithDemoChange && (
                <>
                  <DemoPreviewToggle
                    enabled={previewWithDemo}
                    onChange={handleToggleChange}
                    numberOfRows={localDemoDataSettings.numberOfRows}
                  />
                  {previewWithDemo && (
                    <DemoDataSettingsPopover
                      settings={localDemoDataSettings}
                      onSettingsChange={handleSettingsChange}
                    />
                  )}
                </>
              )}
              <Flex gap="2">
                <Button
                  variant={currentType === 'template' ? 'classic' : 'outlined'}
                  color={currentType === 'template' ? 'primary' : 'default'}
                  startIcon={<IconDownload />}
                  onClick={() => onDownload('template')}
                >
                  Download Template
                </Button>
                <Button
                  variant={currentType === 'demo' ? 'classic' : 'outlined'}
                  color={currentType === 'demo' ? 'primary' : 'default'}
                  startIcon={<IconDownload />}
                  onClick={() => onDownload('demo')}
                >
                  Download Demo
                </Button>
              </Flex>
              <Dialog.Close>
                <Button
                  variant="outlined"
                  size="medium"
                  color="secondary"
                  isIconOnly
                  title="Close"
                  startIcon={<IconX />}
                />
              </Dialog.Close>
            </Flex>
          }
        ></Dialog.Header>

        {/* Main Content: Sidebar + Table */}
        <Flex style={{ flex: 1, overflow: 'hidden' }}>
          {/* Sidebar with Form Info */}
          <Flex
            direction="column"
            style={{
              width: '380px',
              minWidth: '380px',
              borderRight: '1px solid var(--divider)',
              backgroundColor: 'var(--gray-1)',
              overflow: 'hidden',
            }}
          >
            <ScrollArea style={{ height: '100%' }}>
              <Flex
                direction="column"
                gap="3"
                style={{ padding: 'var(--space-4)' }}
              >
                <Text size="2" weight="medium" color="gray">
                  Form Information
                </Text>
                <Separator />
                <StudyFormHoverCard
                  form={form}
                  fieldCount={form.fieldCount}
                  allForms={formsMap}
                  variant="card"
                />

                {previewWithDemo && (
                  <>
                    <Separator />
                    <Card size="none">
                      <CardHeader cardTitle="Demo Data Settings" />
                      <CardContent>
                        <Flex direction="column" gap="2">
                          <Text size="1" color="gray">
                            <strong>Site IDs:</strong>{' '}
                            {localDemoDataSettings.clinicalSiteIds.join(', ')}
                          </Text>
                          <Text size="1" color="gray">
                            <strong>Template:</strong>{' '}
                            {localDemoDataSettings.subjectIdTemplate}
                          </Text>
                          <Text size="1" color="gray">
                            <strong>Rows:</strong>{' '}
                            {localDemoDataSettings.numberOfRows} |{' '}
                            <strong>Start:</strong>{' '}
                            {localDemoDataSettings.startSubjectNumber}
                          </Text>
                        </Flex>
                      </CardContent>
                    </Card>
                  </>
                )}
              </Flex>
            </ScrollArea>
          </Flex>

          {/* CSV Preview Table */}
          <Flex
            direction="column"
            gap="3"
            style={{
              flex: 1,
              overflow: 'hidden',
              padding: 'var(--space-4)',
            }}
          >
            {/* File info */}
            <Flex
              direction="row"
              gap="1"
              style={{
                flexShrink: 0,
                overflow: 'hidden',
              }}
              asChild
            >
              <Heading size="xsmall">
                File: {filename}
                <Badge color="warning" variant="surface" ml="2">
                  Preview mode:{' '}
                  {currentType === 'demo'
                    ? `Demo data (${localDemoDataSettings.numberOfRows} rows)`
                    : 'Empty template'}
                </Badge>
              </Heading>
            </Flex>

            {/* CSV Preview */}
            {csvData ? (
              <CsvContentPreview csvData={csvData} form={form} />
            ) : (
              <Flex align="center" justify="center" style={{ flex: 1 }}>
                <EmptyContent
                  icon={<IconFileText />}
                  title="Unable to parse CSV content"
                  size="small"
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

// Metadata column names
const METADATA_COLUMNS = [
  'subjectid',
  'subjectnumber',
  'siteid',
  'formid',
  'mainformid',
];

// Metadata column labels mapping
const METADATA_LABELS: Record<string, string> = {
  subjectid: 'External Subject ID',
  subjectnumber: 'Subject Number',
  siteid: 'Site ID',
  formid: 'Form ID',
  mainformid: 'Main Form ID',
};

/**
 * Check if a column is a metadata column
 */
function isMetadataColumn(header: string): boolean {
  return METADATA_COLUMNS.includes(header.toLowerCase());
}

/**
 * Get metadata label for a column
 */
function getMetadataLabel(header: string): string | null {
  const lowerHeader = header.toLowerCase();
  return METADATA_LABELS[lowerHeader] || null;
}

// Component to render CSV content preview
type CsvTableRow = { id: string } & Record<string, string>;

const CsvContentPreview: React.FC<{
  csvData: ReturnType<typeof parseCSVContent>;
  form: FormTreeNode;
}> = ({ csvData, form }) => {
  // Determine if form is a subform
  const isSubformForm = form.type === FORM_TYPE_SUBFORM || !!form.parent;

  // Create table data
  const tableData = csvData.rows.map((row, index) => {
    const rowData: CsvTableRow = { id: `row-${index}` };
    csvData.headers.forEach((header, headerIndex) => {
      rowData[`col_${headerIndex}`] = row[headerIndex] || '';
    });
    return rowData;
  });

  // Create columns with metadata highlighting
  const tableColumns: ColumnDef<CsvTableRow>[] = csvData.headers.map(
    (header, index) => {
      const isMetadata = isMetadataColumn(header);
      const metadataLabel = isMetadata ? getMetadataLabel(header) : null;

      return {
        id: `col_${index}`,
        accessorKey: `col_${index}`,
        header: ({ column }) => (
          <Flex align="center" gap="2">
            <DataTableColumnHeader
              column={column}
              title={header || `Column ${index + 1}`}
            />
            {isMetadata && (
              <Badge
                color="blue"
                variant="soft"
                size="xsmall"
                title={metadataLabel || 'Metadata column'}
              >
                M
              </Badge>
            )}
          </Flex>
        ),
        cell: ({ getValue }) => {
          const value = getValue() as string;
          return (
            <Flex
              align="center"
              gap="2"
              style={{
                backgroundColor: isMetadata ? 'var(--blue-1)' : 'transparent',
                padding: isMetadata ? 'var(--space-1)' : '0',
                borderRadius: isMetadata ? 'var(--radius-1)' : '0',
              }}
            >
              <Text
                size="2"
                title={value}
                weight={isMetadata ? 'medium' : 'regular'}
              >
                {value || ''}
              </Text>
            </Flex>
          );
        },
        meta: {
          width: 'auto',
        },
      };
    }
  );

  return (
    <>
      {/* Data table */}
      <DataTable
        scrollMode="table"
        toolbarLeftSlot={
          // Metadata info
          <Flex align="center" gap="2" wrap="wrap">
            <Badge color="blue" variant="soft" size="small">
              M = Metadata
            </Badge>
            <Text size="3xsmall" color="gray">
              {isSubformForm
                ? 'Required metadata columns: subjectid, subjectnumber, siteid, formid, mainformid'
                : 'Required metadata columns: subjectid, subjectnumber, siteid, formid'}
            </Text>
          </Flex>
        }
        data={tableData}
        columns={tableColumns}
        enableSorting={true}
        enableFiltering={true}
        enablePagination={true}
        enableColumnVisibility={false}
        enableRowSelection={false}
        searchPlaceholder="Search CSV data..."
        emptyMessage="No data found"
        pageSizeOptions={[10, 25, 50, 100]}
        getRowId={row => row.id}
      />
    </>
  );
};
